/**
 * The common strip on every position workspace. Status labels are shared across
 * forms, tasks, and seat records so Planning Pulse can group them.
 */

import type { SeatSection } from './seatCatalog';
import { SEAT_CATALOG, type RelatedForm } from './seatCatalog';

export const DELIVERABLE_STATUSES = [
  'Not started',
  'In progress',
  'Ready for review',
  'Ready for the meeting',
  'Needs revision',
  'Approved',
  'Superseded',
  'Complete',
] as const;

export type DeliverableStatus = (typeof DELIVERABLE_STATUSES)[number];

export type Deliverable = {
  id: string;
  title: string;
  status: DeliverableStatus;
  section: SeatSection;
  owner: string;
  due?: string | null;
  blocker?: string | null;
  href?: string;
  restricted?: boolean;
};

export function formDeliverableStatus(
  tip: {
    signatures?: unknown[] | null;
  } | null
): DeliverableStatus {
  if (!tip) return 'Not started';
  if ((tip.signatures?.length ?? 0) > 0) return 'Approved';
  return 'In progress';
}

export function taskDeliverableStatus(
  status: string | null | undefined
): DeliverableStatus {
  switch (status) {
    case 'Closed':
    case 'Complete':
      return 'Complete';
    case 'Submitted for Review':
      return 'Ready for review';
    case 'Approved':
      return 'Approved';
    case 'Blocked':
      return 'In progress';
    case 'Current/Ongoing':
    case 'In Progress':
      return 'In progress';
    case 'Needs Revision':
      return 'Needs revision';
    default:
      return 'Not started';
  }
}

export function formsForSeat(
  related: RelatedForm[],
  tips: { formBaseId: string; signatures?: unknown[] | null }[],
  section: SeatSection,
  owner: string
): Deliverable[] {
  return related.map((form) => {
    const tip = tips.find((row) => row.formBaseId === form.baseId) ?? null;
    return {
      id: `form-${form.baseId}`,
      title: form.label,
      status: formDeliverableStatus(tip),
      section,
      owner,
      href: 'dashboard',
    };
  });
}

export type TaskRow = {
  id: string;
  title: string;
  status: string | null;
  due?: string | null;
  assigneeKind?: string | null;
  assigneeId?: string | null;
  blockedBy?: { action_id: string }[];
};

export function tasksForPositions(
  tasks: TaskRow[],
  positionIds: ReadonlySet<string>,
  section: SeatSection,
  owner: string
): Deliverable[] {
  return tasks
    .filter(
      (task) =>
        task.assigneeKind === 'position' &&
        !!task.assigneeId &&
        positionIds.has(task.assigneeId)
    )
    .map((task) => ({
      id: `task-${task.id}`,
      title: task.title,
      status: taskDeliverableStatus(task.status),
      section,
      owner,
      due: task.due,
      blocker:
        task.blockedBy && task.blockedBy.length > 0
          ? `${task.blockedBy.length} open predecessor${task.blockedBy.length === 1 ? '' : 's'}`
          : null,
      href: `actions/${task.id}`,
    }));
}

export function pulseBySection(
  deliverables: Deliverable[]
): { section: SeatSection; label: string; rows: Deliverable[] }[] {
  const labels: Record<SeatSection, string> = {
    command: 'Command',
    planning: 'Planning',
    operations: 'Operations',
    logistics: 'Logistics',
    finance: 'Finance/Admin',
    intelligence: 'Intelligence and Investigations',
  };
  return (Object.keys(labels) as SeatSection[])
    .map((section) => ({
      section,
      label: labels[section],
      rows: deliverables.filter((row) => row.section === section),
    }))
    .filter((group) => group.rows.length > 0);
}

/**
 * Every catalog form, once, tagged with the section of the first seat that owns
 * it.
 */
export function pulseFormDeliverables(
  tips: { formBaseId: string; signatures?: unknown[] | null }[]
): Deliverable[] {
  const seen = new Set<string>();
  const rows: Deliverable[] = [];
  for (const seat of SEAT_CATALOG) {
    for (const form of seat.relatedForms) {
      if (seen.has(form.baseId)) continue;
      seen.add(form.baseId);
      const tip = tips.find((row) => row.formBaseId === form.baseId) ?? null;
      rows.push({
        id: `form-${form.baseId}`,
        title: form.label,
        status: formDeliverableStatus(tip),
        section: seat.section,
        owner: seat.label,
        href: 'dashboard',
      });
    }
  }
  return rows;
}
